import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import { endPoint } from "utils/config";
import sparkLogo from "assets/img/sparkLogo.svg";
import DialogShell from "designV2/components/DialogShell";
import "./superNav.css";

const SUPER_LOGIN_PATH = "//V1/Spark/api/login/superAdmin";
const OPEN_GROUPS_KEY = "superNavOpenGroups";

/**
 * Everything the sidebar needs to put a number on a link, in one round
 * trip. All four are counts the platform already exposes and that the
 * dashboard alert bar already reads — the point of repeating them here
 * is that the queues stay visible from every screen, not just the
 * dashboard. Polled rather than subscribed: the sidebar is mounted for
 * the whole session and these numbers only need to be roughly current.
 */
const GET_NAV_COUNTS = gql`
  query getSuperNavCounts {
    unrepliedTickets: getConversationsCount(filters: { repliedByAdmin: false, closed: false })
    pendingShopVerifications: getShopsByAdminCount(filters: { verified: false })
    pendingDriverVerifications: getDriversVerificationRequestsCountByAdmin(filters: { status: PENDING })
    failedPayments: getPaymentsByAdminCount(filters: { status: FAILED })
  }
`;

const GET_ADMIN_IDENTITY = gql`
  query getAdminIdentityForSidebar {
    getAdminInformation {
      _id
      fullName
      email
    }
  }
`;

// Which count, if any, belongs on which destination. Keyed by route path
// so a route that gets renamed or moved between groups keeps its badge.
const BADGES = {
  "/tickets": { key: "unrepliedTickets", tone: "danger" },
  "/shopVerifications": { key: "pendingShopVerifications", tone: "warning" },
  "/verifications": { key: "pendingDriverVerifications", tone: "warning" },
  "/failedPayments": { key: "failedPayments", tone: "danger" },
};

const onLogout = () => {
  window.localStorage.removeItem("shopId");
  window.localStorage.removeItem("refreshToken");
  window.localStorage.removeItem("token");
  window.localStorage.removeItem("shopType");
  window.localStorage.removeItem("verificationCode");
  window.localStorage.removeItem("phoneNumber");
  window.localStorage.removeItem("permissions");
  window.location.href = endPoint + SUPER_LOGIN_PATH;
};

const readOpenGroups = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(OPEN_GROUPS_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch (error) {
    return [];
  }
};

/**
 * SuperSidebar - the super admin's left navigation.
 *
 * Driven by the same `routes` array the layout already filters by
 * permission, so nothing here has to know what a super admin is allowed
 * to see. Two things differ from the shop admin sidebar, both because
 * this panel is much bigger: groups collapse (five of them, twenty-odd
 * destinations — permanently open pushed Settings off-screen), and a
 * filter box searches the whole list at once so a known destination is
 * one phrase away instead of an expand-and-scan.
 */
const SuperSidebar = ({ routes, pathname, mobileOpen, onCloseMobile }) => {
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [openGroups, setOpenGroups] = useState(readOpenGroups);

  const { data: countsData } = useQuery(GET_NAV_COUNTS, {
    pollInterval: 60000,
    fetchPolicy: "cache-and-network",
    // Roles are per-permission, so an admin without, say, the payments
    // permission gets an error on that one alias. Without this the whole
    // result is thrown away and every badge disappears with it.
    errorPolicy: "all",
  });
  const counts = countsData || {};

  const { data: adminData } = useQuery(GET_ADMIN_IDENTITY, { errorPolicy: "all" });
  const admin = adminData && adminData.getAdminInformation;

  // Whichever group holds the page you are on opens itself. Without this
  // a deep link (or a dashboard alert) drops you on a page whose group is
  // shut, with no indication of where in the tree you actually are.
  const activeGroup = useMemo(() => {
    const group = routes.find(
      (route) =>
        route.collapse &&
        route.views.some((child) => pathname.indexOf(child.layout + child.path) === 0)
    );
    return group ? group.name : null;
  }, [routes, pathname]);

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((current) => (current.includes(activeGroup) ? current : current.concat(activeGroup)));
  }, [activeGroup]);

  useEffect(() => {
    window.localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (name) =>
    setOpenGroups((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : current.concat(name)
    );

  const badgeCountFor = (route) => {
    const badge = BADGES[route.path];
    if (!badge) return 0;
    return counts[badge.key] || 0;
  };

  const renderLink = (route, key) => {
    const badge = BADGES[route.path];
    const count = badgeCountFor(route);
    return (
      <NavLink
        key={key}
        to={route.layout + route.path}
        className="superNavLink"
        activeClassName="isActive"
        onClick={() => {
          if (window.innerWidth < 960) onCloseMobile();
        }}
      >
        {route.icon && <route.icon className="superNavLinkIcon" fontSize="small" />}
        <span className="superNavLinkLabel">{route.name}</span>
        {count > 0 && (
          <span className={`superNavBadge ${badge.tone === "warning" ? "warning" : ""}`}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </NavLink>
    );
  };

  // Filtering flattens the tree on purpose: once you have typed a name
  // you want the destination, not the branch it happens to live on.
  const query = filter.trim().toLowerCase();
  const matches = (route) => route.name.toLowerCase().indexOf(query) !== -1;

  const filteredLinks = useMemo(() => {
    if (!query) return null;
    const found = [];
    routes.forEach((route) => {
      if (route.redirect) return;
      if (route.collapse) {
        route.views.forEach((child) => {
          if (matches(child) || matches(route)) found.push(child);
        });
        return;
      }
      if (matches(route)) found.push(route);
    });
    return found;
  }, [routes, query]);

  const navList = filteredLinks ? (
    filteredLinks.length ? (
      filteredLinks.map((route, index) => renderLink(route, index))
    ) : (
      <div className="superNavEmpty">No matching page</div>
    )
  ) : (
    routes.map((route, index) => {
      if (route.redirect) return null;
      if (route.collapse) {
        const isOpen = openGroups.includes(route.name);
        const hiddenAlerts = !isOpen && route.views.some((child) => badgeCountFor(child) > 0);
        return (
          <div className="superNavGroup" key={index}>
            <button
              type="button"
              className="superNavGroupToggle"
              onClick={() => toggleGroup(route.name)}
              aria-expanded={isOpen}
            >
              {route.icon && <route.icon className="superNavGroupIcon" fontSize="small" />}
              <span className="superNavGroupName">{route.name}</span>
              {hiddenAlerts && <span className="superNavGroupDot" />}
              <ChevronRightIcon className={`superNavGroupChevron ${isOpen ? "isOpen" : ""}`} />
            </button>
            {isOpen && route.views.map((child, childIndex) => renderLink(child, `${index}-${childIndex}`))}
          </div>
        );
      }
      return renderLink(route, index);
    })
  );

  const content = (
    <React.Fragment>
      <NavLink to="/superAdmin/dashboard" className="superNavBrand">
        <img src={sparkLogo} alt="Spark" />
        <span className="superNavBrandName">Spark</span>
        <span className="superNavBrandTag">Admin</span>
      </NavLink>

      {admin && (
        <div className="superNavAccount">
          <span className="superNavAccountAvatar">
            {(admin.fullName || "?").trim().charAt(0).toUpperCase()}
          </span>
          <span className="superNavAccountText">
            <span className="superNavAccountName">{admin.fullName || "Super admin"}</span>
            <span className="superNavAccountMeta">{admin.email || "Super admin"}</span>
          </span>
        </div>
      )}

      <div className="superNavFilter">
        <SearchIcon />
        <input
          type="text"
          className="superNavFilterInput"
          placeholder="Find a page"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="Filter navigation"
        />
      </div>

      <nav className="superNavList">{navList}</nav>

      <button type="button" className="superNavLogout" onClick={() => setLogoutOpen(true)}>
        <ExitToAppIcon fontSize="small" />
        <span>Log out</span>
      </button>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <aside className="superSidebar superSidebarDesktop">{content}</aside>

      <div className={`superSidebarBackdrop ${mobileOpen ? "isOpen" : ""}`} onClick={onCloseMobile} />
      <aside className={`superSidebar superSidebarMobile ${mobileOpen ? "isOpen" : ""}`}>
        <button
          type="button"
          className="superSidebarCloseButton"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          <CloseIcon fontSize="small" />
        </button>
        {content}
      </aside>

      <DialogShell
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Log out"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setLogoutOpen(false) },
          { label: "Log out", onClick: onLogout, variant: "danger" },
        ]}
      >
        <div className="superNavLogoutConfirm">Do you want to log out?</div>
      </DialogShell>
    </React.Fragment>
  );
};

export default SuperSidebar;
